import "@/App.css";
// Defence-in-depth: monkey-patches sonner's toast to coerce non-string
// args, so it's impossible to render "[object Object]" from any code path.
import "@/lib/safeToast";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Landing from "./pages/Landing";
import DemoBanner from "./components/DemoBanner";
import RequestDemo from "./pages/RequestDemo";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Passenger from "./pages/Passenger";
import Driver from "./pages/Driver";
import DriverEarnings from "./pages/DriverEarnings";
import DriverSubscription from "./pages/DriverSubscription";
import Admin from "./pages/Admin";
import CorporateApply from "./pages/CorporateApply";
import CorporatePortal from "./pages/CorporatePortal";
import PlatformConsole from "./pages/PlatformConsole";
import BecomeOperator from "./pages/BecomeOperator";
import { Toaster } from "./components/ui/sonner";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { initSentry, SentryErrorBoundary } from "./lib/sentry";

// Boot Sentry as soon as App.js loads. Guarded — no-op when the env var
// is blank. Done outside the component so it runs exactly once even
// under StrictMode double-mount.
initSentry();

// Tiny visible fallback that doesn't pretend the app is fine. We rely on
// Sentry to ship the error to the operator; the user just sees a recovery
// link. Avoiding any complex UI here keeps the boundary itself crash-safe.
function FatalErrorFallback({ resetError }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 text-center"
      data-testid="fatal-error-fallback"
    >
      <h1 className="font-heading text-3xl font-black mb-3">SOMETHING BROKE</h1>
      <p className="text-sm text-muted-foreground max-w-md mb-6">
        We've reported the issue. Try reloading — if it keeps happening,
        sign out and back in. Your active ride is safe.
      </p>
      <button
        onClick={() => { resetError?.(); window.location.reload(); }}
        className="px-6 h-10 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider text-xs"
        data-testid="fatal-error-reload-btn"
      >
        Reload
      </button>
    </div>
  );
}

function roleHome(role) {
  if (role === "admin") return "/admin";
  if (role === "driver") return "/driver";
  if (role === "corporate_admin") return "/corporate";
  return "/passenger";
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-mini text-muted-foreground">LOADING…</div>
      </div>
    );
  }
  if (!user) {
    // Keep a Compliance Hold email's "fix documents" target through sign-in.
    const renew = new URLSearchParams(window.location.search).get("renew");
    if (renew) sessionStorage.setItem("driver_renew_doc", renew);
    return <Navigate to="/login" replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

// Active-trip recovery (production hotfix · 2026-06-XX):
// Wraps the three public routes (`/`, `/login`, `/register`) so that an
// already-authenticated user is funnelled back to their role dashboard
// instead of getting trapped on the marketing landing page. The dashboard
// itself fetches `/passenger/active-ride` (Passenger.jsx) or
// `/driver/my-rides` (Driver.jsx) on mount, so a driver mid-trip lands
// straight back on the active-ride UI without any extra wiring.
//
// During the brief AuthContext `loading` window we render children
// optimistically — Landing is a public page, so showing it for a few
// hundred ms while /auth/me is in flight is harmless and avoids a
// visible loading flash. Once `loading` flips to false we either
// redirect (authenticated) or keep rendering children (anonymous).
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return children;
  if (user) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <SentryErrorBoundary
        fallback={({ resetError }) => <FatalErrorFallback resetError={resetError} />}
      >
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" />
            <DemoBanner />
            <UpdatePrompt />
            <Routes>
              <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/corporate/apply" element={<CorporateApply />} />
              <Route path="/become-operator" element={<BecomeOperator />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              {/* /verify-email is NOT wrapped in PublicRoute — authenticated
                  users hitting the link from another browser/tab still need
                  to verify their (different) account, and the page itself
                  handles auto-redirect to the dashboard on success. */}
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Password recovery. /forgot-password is public (signed-out
                  flow); /reset-password is NOT wrapped in PublicRoute so a
                  user clicking the email link while still signed in on
                  another tab isn't bounced to their dashboard. */}
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/passenger" element={<Protected role="passenger"><Passenger /></Protected>} />
              <Route path="/driver" element={<Protected role="driver"><Driver /></Protected>} />
              <Route path="/driver/earnings" element={<Protected role="driver"><DriverEarnings /></Protected>} />
              <Route path="/driver/subscription" element={<Protected role="driver"><DriverSubscription /></Protected>} />
              <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />
              <Route path="/platform" element={<Protected role="admin"><PlatformConsole /></Protected>} />
              <Route path="/corporate" element={<Protected role="corporate_admin"><CorporatePortal /></Protected>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </SentryErrorBoundary>
    </div>
  );
}

export default App;
